/**
 * React Query hooks for NDT invoices (batch 5, fieldiq-api#1166). Same rules as
 * hooks/useNdtInspection.ts: every key starts ['ndtInspection'], every read
 * spreads LOAD_FAILURE_QUERY_OPTIONS, and every write invalidates the NDT
 * prefix — generating or voiding an invoice moves its tickets' status, the
 * Ready-to-invoice list and the register — plus Finance → Invoices, which lists
 * the NDT rows from the unified view.
 *
 * The invoice reads carry no `meta`, so these unwrap `{ data }` alone. A
 * `data: null` stays null (never the envelope).
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidateNdt } from './useNdtInspection';
import type { NdtBillableJob, NdtInvoice, NdtInvoiceGenerate, NdtInvoiceRecipient, NdtInvoiceRow } from '../types/ndt-invoices';
import type { DhRecipient } from '../types/downhole';

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v) out[k] = v;
  return Object.keys(out).length ? out : undefined;
};

/** `{ data }` → data, or null. Never the envelope. */
export function unwrapNdtInvoice<T>(body: unknown): T | null {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return null;
  const b = body as { data?: unknown };
  return b.data === undefined || b.data === null ? null : (b.data as T);
}
const unwrapList = <T,>(body: unknown): T[] => {
  const d = unwrapNdtInvoice<unknown>(body);
  return Array.isArray(d) ? (d as T[]) : [];
};

export const ndtInvoiceKeys = {
  invoices: (p?: Params) => ['ndtInspection', 'invoices', clean(p) ?? {}] as const,
  invoice: (id: string) => ['ndtInspection', 'invoice', id] as const,
  billable: () => ['ndtInspection', 'invoiceBillable'] as const,
  recipient: (id: string) => ['ndtInspection', 'invoiceRecipient', id] as const,
};

function invalidateInvoices(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['finance-invoices-other-verticals'] });
  return invalidateNdt(qc);
}

export function useNdtInvoices(params?: { status?: string; customerId?: string; jobId?: string }, enabled = true) {
  return useQuery({
    queryKey: ndtInvoiceKeys.invoices(params),
    queryFn: async () => unwrapList<NdtInvoiceRow>((await api.ndtInspection.invoices.list(clean(params))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtBillable(enabled = true) {
  return useQuery({
    queryKey: ndtInvoiceKeys.billable(),
    queryFn: async () => unwrapList<NdtBillableJob>((await api.ndtInspection.invoices.billable()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtInvoice(id: string | null | undefined) {
  return useQuery({
    queryKey: ndtInvoiceKeys.invoice(id ?? ''),
    queryFn: async () => unwrapNdtInvoice<NdtInvoice>((await api.ndtInspection.invoices.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Who a send with no `to` would address — the send's own resolver. Reshaped to
 * the snake_case preview the shared RecipientPreview (downhole/sendBits) draws.
 */
export function useNdtInvoiceRecipient(id: string | null | undefined) {
  return useQuery({
    queryKey: ndtInvoiceKeys.recipient(id ?? ''),
    queryFn: async () => {
      const r = unwrapNdtInvoice<NdtInvoiceRecipient>((await api.ndtInspection.invoices.recipient(id as string)).data);
      if (!r) return { data: null };
      const to: DhRecipient | null = r.to ? { name: r.to.name, email: r.to.email, source: r.to.source, source_label: r.to.sourceLabel } : null;
      return {
        data: {
          to,
          message: r.message,
          last_sent: r.lastSent ? { at: r.lastSent.at, to: r.lastSent.to, cc: r.lastSent.cc } : null,
        },
      };
    },
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function useInvoiceWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateInvoices(qc) });
}

export const useGenerateNdtInvoice = () =>
  useInvoiceWrite(async (body: NdtInvoiceGenerate) => unwrapNdtInvoice<NdtInvoice>((await api.ndtInspection.invoices.generate(body)).data));
export const useSendNdtInvoice = () =>
  useInvoiceWrite(async ({ id, to, cc }: { id: string; to?: string; cc?: string[] }) =>
    unwrapNdtInvoice<NdtInvoice & { sentToName?: string | null }>(
      (await api.ndtInspection.invoices.send(id, { ...(to ? { to } : {}), ...(cc && cc.length ? { cc } : {}) })).data,
    ),
  );
export const useVoidNdtInvoice = () =>
  useInvoiceWrite(async ({ id, reason }: { id: string; reason: string }) =>
    unwrapNdtInvoice<NdtInvoice>((await api.ndtInspection.invoices.void(id, reason)).data),
  );
export const useMarkNdtInvoicePaid = () => useInvoiceWrite(async (id: string) => (await api.ndtInspection.invoices.markPaid(id)).data);

/**
 * The live invoice an INVOICED ticket is on. The ticket read does not name it,
 * so this reads the job's invoices and opens the non-void ones until one lists
 * the ticket unreleased. Usually one invoice per job, so usually one detail
 * read. Null when none does (never a guess).
 */
export function useNdtTicketInvoice(ticketId: string | null | undefined, jobId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['ndtInspection', 'ticketInvoice', ticketId ?? '', jobId ?? ''] as const,
    queryFn: async () => {
      const rows = unwrapList<NdtInvoiceRow>((await api.ndtInspection.invoices.list({ jobId: jobId as string })).data);
      for (const r of rows.filter((x) => x.status !== 'void')) {
        const inv = unwrapNdtInvoice<NdtInvoice>((await api.ndtInspection.invoices.get(r.id)).data);
        if (inv?.tickets.some((t) => t.id === ticketId && !t.releasedAt)) return { id: inv.id, invoiceNumber: inv.invoiceNumber, status: inv.status };
      }
      return null;
    },
    enabled: enabled && !!ticketId && !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
